/**
 * TranslationService
 * 
 * Handles loading and managing translations for the application.
 * Supports English (en) and Chinese (zh) languages.
 * Uses in-memory caching for loaded translations.
 * Implements fallback to English for missing Chinese translations.
 */

import TranslationErrorHandler, { TranslationErrorType } from '../utils/translationErrorHandler';

type SupportedLanguage = 'en' | 'zh';

interface TranslationMap {
  [key: string]: string | TranslationMap;
}

interface TranslationCache {
  [language: string]: TranslationMap;
}

class TranslationService {
  private static cache: TranslationCache = {};
  private static loadingPromises: Map<string, Promise<TranslationMap>> = new Map();

  /**
   * Load translations for a specific language
   * Uses in-memory caching to avoid repeated loads
   */
  static async loadTranslations(language: SupportedLanguage): Promise<TranslationMap> {
    // Return from cache if already loaded
    if (this.cache[language]) {
      return this.cache[language];
    }

    // Return existing loading promise if already in progress
    if (this.loadingPromises.has(language)) {
      return this.loadingPromises.get(language)!;
    }

    // Start loading
    const loadingPromise = this.fetchTranslations(language);
    this.loadingPromises.set(language, loadingPromise);

    try {
      const translations = await loadingPromise;
      this.cache[language] = translations;
      return translations;
    } catch (error) {
      TranslationErrorHandler.handleMissingTranslationFile(
        language,
        error instanceof Error ? error : new Error(String(error))
      );
      
      // If loading Chinese fails, fallback to English
      if (language === 'zh') {
        console.warn('Falling back to English translations');
        return this.loadTranslations('en');
      }
      
      // If English fails, return empty object
      return {};
    } finally {
      this.loadingPromises.delete(language);
    }
  }

  /**
   * Fetch translations from JSON file
   */
  private static async fetchTranslations(language: SupportedLanguage): Promise<TranslationMap> {
    try {
      // Add cache-busting parameter to force fresh fetch
      const cacheBuster = Date.now();
      const response = await fetch(`/locales/${language}.json?v=${cacheBuster}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const translations = await response.json();
      return translations;
    } catch (error) {
      console.error(`Error fetching translations for ${language}:`, error);
      throw error;
    }
  }

  /**
   * Get translated string for a given key
   * Supports nested keys using dot notation (e.g., "common.welcome")
   * Supports parameter interpolation (e.g., "Hello {{name}}")
   * Falls back to English if translation is missing in the target language
   */
  static translate(
    key: string,
    language: SupportedLanguage,
    params?: Record<string, any>
  ): string {
    // Validate language code
    if (language !== 'en' && language !== 'zh') {
      const validLanguage = TranslationErrorHandler.handleInvalidLanguage(language);
      return this.translate(key, validLanguage, params);
    }

    // Get translation from cache
    const translations = this.cache[language];
    
    if (!translations) {
      console.warn(`Translations not loaded for language: ${language}`);
      return TranslationErrorHandler.handleMissingTranslation(key, language, params);
    }

    // Navigate nested keys
    const value = this.getNestedValue(translations, key);

    // If translation not found and language is Chinese, fallback to English
    if (value === undefined && language === 'zh') {
      const englishTranslations = this.cache['en'];
      if (englishTranslations) {
        const englishValue = this.getNestedValue(englishTranslations, key);
        if (englishValue !== undefined) {
          return this.interpolate(englishValue, params);
        }
      }
    }

    // If still not found, use error handler
    if (value === undefined) {
      return TranslationErrorHandler.handleMissingTranslation(key, language, params);
    }

    // If value is not a string, use error handler
    if (typeof value !== 'string') {
      return TranslationErrorHandler.handleInvalidKey(key, language);
    }

    // Interpolate parameters
    return this.interpolate(value, params);
  }

  /**
   * Get nested value from object using dot notation
   */
  private static getNestedValue(obj: TranslationMap, path: string): string | undefined {
    const keys = path.split('.');
    let current: any = obj;

    for (const key of keys) {
      if (current === undefined || current === null) {
        return undefined;
      }
      current = current[key];
    }

    return typeof current === 'string' ? current : undefined;
  }

  /**
   * Interpolate parameters in translation string
   * Replaces {{paramName}} with actual values
   */
  private static interpolate(text: string, params?: Record<string, any>): string {
    if (!params) {
      return text;
    }

    return text.replace(/\{\{(\w+)\}\}/g, (match, paramName) => {
      const value = params[paramName];
      return value !== undefined ? String(value) : match;
    });
  }

  /**
   * Check if a translation exists for a given key
   */
  static hasTranslation(key: string, language: SupportedLanguage): boolean {
    const translations = this.cache[language];
    if (!translations) {
      return false;
    }

    const value = this.getNestedValue(translations, key);
    return value !== undefined;
  }

  /**
   * Preload translations for a language in the background
   */
  static async preloadLanguage(language: SupportedLanguage): Promise<void> {
    try {
      await this.loadTranslations(language);
    } catch (error) {
      console.error(`Failed to preload language: ${language}`, error);
    }
  }

  /**
   * Clear all cached translations
   */
  static clearCache(): void {
    this.cache = {};
    this.loadingPromises.clear();
  }

  /**
   * Get all cached languages
   */
  static getCachedLanguages(): SupportedLanguage[] {
    return Object.keys(this.cache) as SupportedLanguage[];
  }
}

/**
 * LocalStorage utilities for language preferences and content metadata
 */
class LanguageStorage {
  // localStorage keys
  private static readonly UI_LANGUAGE_KEY = 'app_ui_language';
  private static readonly CONTENT_LANGUAGE_KEY = 'app_content_language';
  private static readonly CONTENT_LANGUAGE_PREFIX = 'content_language_';

  /**
   * Get UI language preference from localStorage
   * Defaults to 'en' if not set
   */
  static getUILanguage(): SupportedLanguage {
    try {
      const stored = localStorage.getItem(this.UI_LANGUAGE_KEY);
      if (stored === 'en' || stored === 'zh') {
        return stored;
      }
      return 'en';
    } catch (error) {
      TranslationErrorHandler.handleCacheError(
        'getUILanguage',
        error instanceof Error ? error : new Error(String(error))
      );
      return 'en';
    }
  }

  /**
   * Set UI language preference in localStorage
   */
  static setUILanguage(language: SupportedLanguage): void {
    try {
      localStorage.setItem(this.UI_LANGUAGE_KEY, language);
    } catch (error) {
      TranslationErrorHandler.handleCacheError(
        'setUILanguage',
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Get content language preference from localStorage
   * Defaults to 'en' if not set
   */
  static getContentLanguage(): SupportedLanguage {
    try {
      const stored = localStorage.getItem(this.CONTENT_LANGUAGE_KEY);
      if (stored === 'en' || stored === 'zh') {
        return stored;
      }
      return 'en';
    } catch (error) {
      TranslationErrorHandler.handleCacheError(
        'getContentLanguage',
        error instanceof Error ? error : new Error(String(error))
      );
      return 'en';
    }
  }

  /**
   * Set content language preference in localStorage
   */
  static setContentLanguage(language: SupportedLanguage): void {
    try {
      localStorage.setItem(this.CONTENT_LANGUAGE_KEY, language);
    } catch (error) {
      TranslationErrorHandler.handleCacheError(
        'setContentLanguage',
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Get language metadata for specific content
   * Returns null if not set
   */
  static getContentLanguageMetadata(contentId: string): SupportedLanguage | null {
    try {
      const key = `${this.CONTENT_LANGUAGE_PREFIX}${contentId}`;
      const stored = localStorage.getItem(key);
      if (stored === 'en' || stored === 'zh') {
        return stored;
      }
      return null;
    } catch (error) {
      console.error(`Error reading content language metadata for ${contentId}:`, error);
      return null;
    }
  }

  /**
   * Set language metadata for specific content
   */
  static setContentLanguageMetadata(contentId: string, language: SupportedLanguage): void {
    try {
      const key = `${this.CONTENT_LANGUAGE_PREFIX}${contentId}`;
      localStorage.setItem(key, language);
    } catch (error) {
      console.error(`Error saving content language metadata for ${contentId}:`, error);
    }
  }

  /**
   * Remove language metadata for specific content
   */
  static removeContentLanguageMetadata(contentId: string): void {
    try {
      const key = `${this.CONTENT_LANGUAGE_PREFIX}${contentId}`;
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing content language metadata for ${contentId}:`, error);
    }
  }

  /**
   * Get all content language metadata
   * Returns a map of contentId -> language
   */
  static getAllContentLanguageMetadata(): Map<string, SupportedLanguage> {
    const metadata = new Map<string, SupportedLanguage>();
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CONTENT_LANGUAGE_PREFIX)) {
          const contentId = key.substring(this.CONTENT_LANGUAGE_PREFIX.length);
          const language = localStorage.getItem(key);
          if (language === 'en' || language === 'zh') {
            metadata.set(contentId, language);
          }
        }
      }
    } catch (error) {
      console.error('Error reading all content language metadata:', error);
    }
    
    return metadata;
  }

  /**
   * Clear all language preferences and metadata
   */
  static clearAll(): void {
    try {
      localStorage.removeItem(this.UI_LANGUAGE_KEY);
      localStorage.removeItem(this.CONTENT_LANGUAGE_KEY);
      
      // Remove all content language metadata
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CONTENT_LANGUAGE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing language data from localStorage:', error);
    }
  }

  /**
   * Check if localStorage is available
   */
  static isAvailable(): boolean {
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }
}

export default TranslationService;
export { LanguageStorage, TranslationErrorHandler, TranslationErrorType };
export type { SupportedLanguage, TranslationMap };
